<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { CircleCloseFilled, Clock, Close, Minus, RemoveFilled, SuccessFilled, Timer } from '@element-plus/icons-vue'
import {
    useFetchApiRequestAsync,
    useOrganizations,
    OrganizationMs,
    useProfileUpdate,
    useActiveOrganization
} from 'auth-package'
import OrganizationAvatar from '@/components/utilities/OrganizationAvatar.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import i18n from '@/i18n'
import { useInvitationManagerStore } from '@/stores/setting/Invitation'

const { t } = i18n.global
const activeOrganization = useActiveOrganization()

const invitationStore = useInvitationManagerStore();

onMounted(() => {
    console.log("activeOrganization", activeOrganization.value.uuid);
    if (activeOrganization) {
        invitationStore.getInvitationList(activeOrganization.value.uuid);
    }
})

watch(activeOrganization, () => {
    console.log("Change organization");
    invitationStore.getInvitationList(activeOrganization.value.uuid);
});

function revokeInvitationConfirmBox(invitationId: string) {
    ElMessageBox.confirm(t('settings.invitation.revokeNotice'), t('settings.warning'), {
        confirmButtonText: t('settings.invitation.confirmRevokeInvitation'),
        cancelButtonText: t('settings.invitation.cancelRevokeInvitation'),
        type: 'warning'
    }).then(async () => {
        const response = await invitationStore.revokeInvitation(activeOrganization.value.uuid, invitationId)

        if (response) {
            ElMessage({
                type: 'success',
                message: t('settings.invitation.successRevoke')
            })
        } else {
            ElMessage({
                type: 'error',
                message: t('settings.invitation.errorRevoke')
            })
        }
    })
}


function getStatusToTypeMap(status: any) {
    const statusTypeMap: any = {
        pending: "info",
        expired: "warning",
        declined: "error",
        revoked: "error",
        accepted: "success",
    };

    return statusTypeMap[status] ?? "error";
}

function getStatusToIconMap(status: any) {
    const statusTypeMap: any = {
        pending: Timer,
        expired: Clock,
        declined: CircleCloseFilled,
        revoked: RemoveFilled,
        accepted: SuccessFilled,
    };

    return statusTypeMap[status] ?? "error";
}


</script>

<template>
    <el-table class="organization-table" :data="invitationStore.invitations" style="width: 100%">
        <el-table-column type="email" :label="$t('settings.invitation.email')" class="d-flex align-items-center gap-2">
            <template #default="props">
                <div class="d-inline-flex flex-column ps-1">
                    <div>
                        {{ props.row.email }}
                    </div>
                </div>
            </template>
        </el-table-column>


        <el-table-column type="scopes" :label="$t('settings.invitation.scopes')" class="d-flex align-items-center">
            <template #default="props">
                <div class="d-flex flex-row flex-wrap ps-1 gap-2">
                    <el-tag type="info" v-for="scope in props.row.roles">
                        {{ $t('settings.userManager.organization.scope.' + scope) }}
                    </el-tag>
                </div>
            </template>
        </el-table-column>

        <el-table-column type="status" :label="$t('settings.invitation.status')"
            class="d-flex align-items-center gap-2">
            <template #default="props">
                <div class="d-inline-flex flex-column ps-1">
                    <el-alert :title="$t('settings.invitation.statuses.' + props.row.status)"
                        :type="getStatusToTypeMap(props.row.status)" center show-icon :closable="false">
                        <template #icon>
                            <component :is="getStatusToIconMap(props.row.status)" />
                        </template>
                    </el-alert>


                </div>
            </template>
        </el-table-column>

        <el-table-column width="56">
            <template #default="scope">
                <el-tooltip :content="$t('settings.invitation.revoke')">
                    <el-button @click="revokeInvitationConfirmBox(scope.row.id)" type="danger" :icon="Close" circle
                        :disabled="scope.row.status != 'pending'" text />
                </el-tooltip>
            </template>
        </el-table-column>
    </el-table>
</template>

<style lang="scss">
.active-row {
    --el-table-tr-bg-color: var(--el-color-primary-light-9);
}

.organization-table {
    .el-table__row {
        .cell {
            gap: 8px;
            display: flex;
            align-items: center;
        }
    }
}

.active-name {
    color: var(--el-color-primary);
}
</style>
